import { createContext, useContext, useState, useEffect, useRef } from "react";
import { io } from "socket.io-client";
import { useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";

import { refreshTokenApi } from "@/api/auth";
import { deleteFcmToken, postLogOut } from "@/api/misc";
import { resetPosthog } from "@/services/posthog";
import { SOCKET_URL } from "@/config/vars";
import { getLocalUserData } from "@/utils";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { readChatMessage, updateChatEnded, updateChatListItem, getChatStats, getChats, markUserAsDeleted, clearChats } from "@/store/slices/chatSlice";
import { addNewNotification, clearNotifications, updateNotificationData } from "@/store/slices/notificationSlice";
import { updateSuspensionStatus, updateBanStatus, logout } from "@/store/slices/authSlice";
import { clearSubscriptions } from "@/store/slices/purchaseSlice";
import { Message, IChatEnded } from "@/types/chat";

const SocketContext = createContext(null);

// Socket Hook
export const useSocket = () => {
  return useContext(SocketContext);
};

// Socket Provider Initialization
export const SocketProvider = ({ initialAccessToken, existingRefreshToken, children }) => {
  const [token, setToken] = useState(initialAccessToken);
  const [socket, setSocket] = useState(null);

  useEffect(() => {
    setToken(initialAccessToken || "");
  }, [initialAccessToken]);

  useEffect(() => {
    if (!token) {
      setSocket(null);
      return;
    }

    const newSocket = io(SOCKET_URL, {
      auth: { token },
      transports: ["websocket"],
    });

    newSocket.on("connect", () => {
      console.log("Connected to Socket.IO server");
    });

    newSocket.on("connect_error", async (err) => {
      try {
        if (err.message === "Invalid Token.") {
          const userData = getLocalUserData();

          const { access_token } = await refreshTokenApi({ refresh_token: userData.refresh_token });
          setToken(access_token);
          newSocket.auth = { token: access_token };
          newSocket.connect();
        }
      } catch (error) {
        console.log(error);
      }
    });

    newSocket.connect();
    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, [token, existingRefreshToken]);

  return (
    <SocketContext.Provider value={socket}>
      <GlobalChatListListener socket={socket} />
      {children}
    </SocketContext.Provider>
  );
};

// Global Chat List Listener Component
const GlobalChatListListener = ({ socket }) => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const userData = useAppSelector((state) => state.auth.userData);
  const { user } = userData || {};
  const refresh_token = userData?.refresh_token;
  const { list } = useAppSelector((state) => state.chat) || { list: [] };
  const listRef = useRef(list);

  useEffect(() => {
    listRef.current = list;
  }, [list]);

  useEffect(() => {
    if (!socket || !user?.id) return;

    const handleMessage = (message: Message) => {

      const shouldIncrementUnread = message.sender_id !== user.id && message.status === 'unread';

      const chatExists = listRef.current.some(chat => chat.id === message.chat_id);


      const lastMessage = {
        id: message.id,
        sender: message.sender_ref,
        sender_id: message.sender_id,
        type: message.type,
        text: message.text,
        files: message.files,
        created_at: message.created_at,
        updated_at: message.updated_at,
      };

      if (chatExists) {

        dispatch(
          updateChatListItem({
            chatId: message.chat_id,
            lastMessage,
            incrementUnread: shouldIncrementUnread,
            timestamp: message.created_at,
            moveToTop: true,
          }),
        );
      } else if (shouldIncrementUnread) {

        dispatch(
          getChats({
            searchQuery: '',
            organizationId: user?.organization?.id || null,
            userId: user?.id || null,
            append: false,
          }),
        );
      }


      if (shouldIncrementUnread) {
        dispatch(getChatStats());
      }
    };

    const handleUpdatedMessage = (updatedMessage: Message) => {

      const lastMessage = {
        id: updatedMessage.id,
        sender: updatedMessage.sender_ref,
        sender_id: updatedMessage.sender_id,
        type: updatedMessage.type,
        text: updatedMessage.text,
        files: updatedMessage.files,
        created_at: updatedMessage.created_at,
        updated_at: updatedMessage.updated_at,
      };

      dispatch(
        updateChatListItem({
          chatId: updatedMessage.chat_id,
          lastMessage,
          incrementUnread: false,
          timestamp: updatedMessage.updated_at,
          moveToTop: true,
        }),
      );
    };

    const handleChatEnded = (data: IChatEnded) => {
      dispatch(updateChatEnded({
        organization_id: data.organization_id,
        job_id: data.job_id,
        jobseeker_id: data.jobseeker_id,
        ended: data.ended
      }));
    };

    const handleChatRead = (data: { chat_id: string; user_id: string }) => {

      if (data.user_id === user.id) {
        dispatch(readChatMessage(data.chat_id));
      }
    };

    const handleMessageRead = (data: { chat_id: string; user_id: string; message_ids: string[] }) => {

      if (data.user_id === user.id) {
        dispatch(readChatMessage(data.chat_id));
      }
    };

    const handleNewNotification = async (notificationData: any) => {
      if (notificationData.user_id !== user.id) {
        return;
      }

      try {
        const notification = {
          id: notificationData._id,
          message: notificationData.message,
          read: notificationData.read || false,
          type: notificationData.type,
          data: notificationData.data,
          created_at: notificationData.created_at,
        };

        dispatch(addNewNotification(notification));

        // Handle account suspension in real-time
        if (notificationData.type === 'account_suspended') {
          dispatch(updateSuspensionStatus({
            is_suspended: true,
            suspension_end_date: notificationData.data?.suspension_end_date || null,
            suspension_reason: notificationData.data?.category || '',
          }));
        }

        // Handle account ban in real-time
        if (notificationData.type === 'account_banned') {
          dispatch(updateBanStatus({
            is_banned: true,
            ban_reason: notificationData.data?.category || '',
          }));
          await deleteFcmToken(user.id);
          await postLogOut(refresh_token);
          Sentry.setUser(null);
          dispatch(clearNotifications());
          dispatch(clearChats());
          queryClient.clear();
          queryClient.removeQueries();
          dispatch(clearSubscriptions());
          dispatch(logout());
          resetPosthog();
          window.location.reload();
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    };

    const handleUpdatedNotification = (data: any) => {
      if (data?._id && data?.data) {
        dispatch(updateNotificationData({ id: data._id, data: data.data }));
      }
    };

    const handleUserDeleted = (deletedUserData: any) => {

      if (deletedUserData?.id && deletedUserData?.type) {
        const userType = deletedUserData.type === 'organization' ? 'organization' : 'jobseeker';

        dispatch(markUserAsDeleted({
          userId: deletedUserData.id,
          userType
        }));

        queryClient.invalidateQueries({ queryKey: ['chat'] });
      }
    };



    socket.on('message', handleMessage);
    socket.on('updated_message', handleUpdatedMessage);
    socket.on('chat_ended', handleChatEnded);
    socket.on('chat_read', handleChatRead);
    socket.on('messages_read', handleMessageRead);
    socket.on('new_notification', handleNewNotification);
    socket.on('updated_notification', handleUpdatedNotification);
    socket.on('user_deleted', handleUserDeleted);

    return () => {
      socket.off('message', handleMessage);
      socket.off('updated_message', handleUpdatedMessage);
      socket.off('chat_ended', handleChatEnded);
      socket.off('chat_read', handleChatRead);
      socket.off('messages_read', handleMessageRead);
      socket.off('new_notification', handleNewNotification);
      socket.off('updated_notification', handleUpdatedNotification);
      socket.off('user_deleted', handleUserDeleted);
    };
  }, [socket, user?.id, dispatch, queryClient]);

  return null;
};
